// app/javascript/components/quotes/builder/components/TreeCard.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQuoteEditor } from "../context/QuoteEditorContext"
import useSortable from "../hooks/useSortable"
import { collectMatches } from "../helpers/search"
import { leafOrder, moveTargets, rangeBetween } from "../helpers/selection"
import { isEditingText } from "../hooks/useHistoryShortcuts"
import { TreeViewProvider } from "../context/TreeViewContext"
import ColHead from "./ColHead"
import NodeRenderer from "./NodeRenderer"
import AddBar from "./AddBar"
import SearchBar from "./SearchBar"
import SelectionBar from "./SelectionBar"
import Card from "@react/shared/ui/Card"

const TreeCard = () => {
  const { state, dispatch } = useQuoteEditor()
  const sections = (state.sections || []).filter(s => !s._destroy)
  const [searchOpen, setSearchOpen] = useState(false)
  const scrollRef = useRef(null)

  const handleReorderTop = useCallback((fromIndex, toIndex) => {
    dispatch({
      type: "REORDER_NODE",
      payload: { parentUid: null, fromIndex, toIndex },
    })
  }, [dispatch])

  // Every child of the root is draggable: the index SortableJS returns only
  // counts the draggable elements, so it must stay the one of the array.
  const treeRef = useSortable(handleReorderTop, { draggable: ".qb-node" })

  const query = state.search.query
  const { matches, relevant, ordered } = useMemo(
    () => collectMatches(state.sections, query),
    [state.sections, query]
  )

  const searching = query.trim().length > 0
  const cursor    = ordered.length ? Math.min(state.search.cursor, ordered.length - 1) : 0
  const currentUid = ordered[cursor] || null

  const order   = useMemo(() => leafOrder(state.sections), [state.sections])
  const targets = useMemo(() => moveTargets(state.sections), [state.sections])
  const selected = useMemo(() => new Set(state.selection), [state.selection])
  const lastClicked = useRef(null)

  const orderRef     = useRef(order)
  const selectionRef = useRef(state.selection)
  orderRef.current     = order
  selectionRef.current = state.selection

  const toggleRow = useCallback((uid, withShift) => {
    if (withShift && lastClicked.current) {
      const range = rangeBetween(orderRef.current, lastClicked.current, uid)
      const merged = new Set(selectionRef.current)
      // Shift+click adds the range; it never unchecks.
      range.forEach(u => merged.add(u))
      dispatch({ type: "SET_SELECTION", payload: { uids: [...merged] } })
    } else {
      dispatch({ type: "TOGGLE_SELECTION", payload: { uid } })
    }
    lastClicked.current = uid
  }, [dispatch])

  const selecting = state.selection.length > 0

  const search = useMemo(
    () => (searching ? { matches, relevant, currentUid } : null),
    [searching, matches, relevant, currentUid]
  )

  const closeSearch = useCallback(() => {
    setSearchOpen(false)
    dispatch({ type: "SET_SEARCH", payload: { query: "" } })
  }, [dispatch])

  // Cmd/Ctrl+F opens our search: the browser one only sees what is rendered and
  // would miss a collapsed section.
  useEffect(() => {
    const onKey = event => {
      if (event.key?.toLowerCase() !== "f") return
      if (!(event.metaKey || event.ctrlKey)) return
      event.preventDefault()
      setSearchOpen(true)
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [])

  useEffect(() => {
    if (!searchOpen) return undefined
    const onKey = event => {
      if (event.key !== "Escape") return
      if (isEditingText(event.target)) return
      closeSearch()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [searchOpen, closeSearch])

  // Waits a frame: the rows a search opens render after this commit.
  useEffect(() => {
    if (!currentUid) return undefined
    const frame = requestAnimationFrame(() => {
      const row = scrollRef.current?.querySelector(`[data-uid="${currentUid}"]`)
      row?.scrollIntoView({ block: "center", behavior: "smooth" })
    })
    return () => cancelAnimationFrame(frame)
  }, [currentUid])

  return (
    <Card className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {searchOpen && (
        <div className="flex items-center justify-end px-3 py-2 border-b border-slate-200 bg-slate-50/60 shrink-0">
          <SearchBar
            query    = {query}
            total    = {ordered.length}
            cursor   = {cursor}
            onChange = {value => dispatch({ type: "SET_SEARCH", payload: { query: value } })}
            onMove   = {delta => dispatch({ type: "MOVE_SEARCH_CURSOR", payload: { total: ordered.length, delta } })}
            onClose  = {closeSearch}
          />
        </div>
      )}

      <ColHead onOpenSearch={() => setSearchOpen(true)} />

      <div
        ref            = {scrollRef}
        data-searching = {searching ? "1" : undefined}
        data-selecting = {selecting ? "1" : undefined}
        className      = "flex-1 min-h-0 overflow-y-auto"
      >
        {/* Inside the scroller, never on it: the wheel needs a live target. */}
        <TreeViewProvider selected={selected} search={search} onToggle={toggleRow} scrollRef={scrollRef}>
          {/* Read-only still folds and unfolds: everything but the twists is out of reach. */}
          <div
            ref       = {treeRef}
            {...(state.readOnly ? {
              onFocusCapture: e => { if (!e.target.closest("[data-twist]")) e.target.blur() },
              className:      "[&_*]:pointer-events-none [&_[data-twist]]:pointer-events-auto",
            } : {})}
          >
            {sections.map((section, i) => (
              <NodeRenderer
                key   = {section._uid}
                node  = {section}
                path  = {[i + 1]}
                depth = {0}
              />
            ))}
          </div>
        </TreeViewProvider>
      </div>

      {state.readOnly ? null : selecting ? (
        <SelectionBar
          count       = {state.selection.length}
          targets     = {targets}
          onDuplicate = {() => dispatch({ type: "DUPLICATE_SELECTED" })}
          onMove      = {uid => dispatch({ type: "MOVE_SELECTED", payload: { targetUid: uid } })}
          onDelete    = {() => dispatch({ type: "DELETE_SELECTED" })}
          onClear     = {() => dispatch({ type: "CLEAR_SELECTION" })}
        />
      ) : (
        <AddBar />
      )}
    </Card>
  )
}

export default TreeCard
